/**
 * PatientPicker — searchable single-select dropdown that returns a
 * `PatientDatum` from the patient list.
 *
 * Designed for use OUTSIDE the patient module — scheduling
 * "create appointment", studio "send form to patient", etc.
 * (Flutter `select_patient_widget.dart`.)
 *
 * Implementation notes:
 *   - Reuses `usePatientsList` for the query. Server-side search via
 *     the `search` param (Flutter does the same).
 *   - Debounced input (250 ms). **Search query is NOT fired below 2
 *     chars** — protects single-character / accidental-keystroke
 *     queries from leaking name fragments to the server log + saves
 *     a round-trip. Samer PR #101 review 2026-05-14.
 *   - Returns up to 20 results — matches Flutter's `pageSize: 20`.
 *   - Filters out archived patients by default (clinical UX — don't
 *     surface archived records in scheduling). Override via
 *     `includeArchived={true}` — typed as a strict boolean (no
 *     undefined fall-through to truthy default).
 *   - Keyboard: Up/Down navigate, Enter selects, Escape closes
 *     (provided by `cmdk` under shadcn `Command`).
 *   - Accessible: button acts as combobox trigger with
 *     `aria-haspopup`, listbox semantics from `cmdk`.
 *
 * Display per row: photo / name / MRN / DOB / phone — Flutter parity.
 *
 * **PHI handling (Samer PR #101 review):**
 *   - `PatientDatum` contains PHI (name, MRN, DOB, phone). Do NOT
 *     console.log selections or search queries from consumers.
 *   - The search query may include name/MRN fragments; do not pipe it
 *     to analytics breadcrumbs (Sentry, Mixpanel, etc.) without
 *     redaction.
 *   - Tenant context comes EXCLUSIVELY from `useAuthStore` — no prop
 *     override, no way for a caller to query a different org's
 *     patients. Cross-tenant exposure is a server-side
 *     authorization concern; the client never names the org.
 */

import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Skeleton,
} from '@scripsteam/scrips-signal-ds';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { V2_META } from '@/shared/ui/design-v2';
import { identityFacts } from '@/patients/lib/identity-facts';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { usePatientsList } from '@/patients/services/use-patients-list';
import { cn } from '@/lib/utils';
import type { PatientDatum } from '@/types/patient-api';

/** Minimum characters before firing a search against the server.
 *  Below this, the popover shows the "start typing" empty state and no
 *  network call goes out. Protects against single-character / accidental
 *  queries leaking name fragments. */
const MIN_SEARCH_CHARS = 2;

export interface PatientPickerProps {
  /** Currently selected patient (or null when nothing is picked). */
  value: PatientDatum | null;
  /** Fires with the chosen patient. Pass `null` to clear. */
  onChange: (next: PatientDatum | null) => void;
  /** Placeholder text on the trigger when nothing is selected. */
  placeholder?: string;
  /** Include archived patients in the results. Default `false` — strict
   *  boolean (no undefined). Pass `true` only in flows that need to
   *  surface archived records (e.g. an admin-only view); never in
   *  clinical scheduling, where archived patients are out-of-scope. */
  includeArchived: boolean;
  /** Patients never offered in the list (for example, the patient a document is already on). */
  excludePatientIds?: readonly string[];
  /** Disable the trigger entirely. */
  disabled?: boolean;
  /** Optional class for the trigger button. */
  className?: string;
}

export function PatientPicker({
  value,
  onChange,
  placeholder = 'Select a patient…',
  includeArchived,
  excludePatientIds,
  disabled,
  className,
}: PatientPickerProps) {
  // Tenant context is read from the auth store ONLY. The caller cannot
  // override the org via props — a reusable picker that accepted an
  // org-id override would let any consumer query patients in tenants
  // they're not authenticated for. Samer PR #101 review 2026-05-14.
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 250);

  // Clear the search when the popover closes so reopening shows
  // recent-results, not the previous query's hits.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Min-2-char gate (Samer PR #101 review): suppress the query below
  // MIN_SEARCH_CHARS so single-keystroke name fragments don't hit the
  // server log. The "start typing" empty state covers this case.
  const searchQuery =
    debounced.trim().length >= MIN_SEARCH_CHARS ? debounced.trim() : undefined;

  const { data, isFetching } = usePatientsList({
    organizationId: orgId,
    pageNumber: 1,
    pageSize: 20,
    search: searchQuery,
    isArchived: includeArchived ? undefined : false,
  });
  const rows = (data?.patientData ?? []).filter((p) => !p.patientId || !excludePatientIds?.includes(p.patientId));

  const displayName = (p: PatientDatum) =>
    p.name ||
    [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') ||
    'Unnamed patient';

  // PROD-2743 design sheet §1 Identity / §2.2: with the switch ON the picker shows the person
  // first (name, then age · sex) and the MRN once, last and quiet. OFF keeps today's MRN line.

  return (
    <AnchoredPopover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      {/* BaseUI Popover uses `render={...}` to project props onto a custom
          element — equivalent to Radix `asChild`. The Trigger MUST get a
          single DOM child, hence we pass a button via `render` rather
          than wrapping. */}
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            disabled={disabled}
            className={cn(
              'pa-picker-trigger inline-flex items-center justify-between gap-2 w-full px-3 py-2 rounded-md border bg-surface-card',
              'border-border-default hover:border-brand-primary',
              'text-sm text-left transition-colors',
              disabled && 'opacity-60 cursor-not-allowed',
              className,
            )}
          />
        }
      >
        {value ? (
          <span className="flex items-center gap-2 min-w-0">
            <Avatar
              scale="compact"
              tone="patient"
              name={displayName(value)}
              src={value.image ?? undefined}
              size="sm"
            />
            <span className="min-w-0">
              <span className="block truncate text-text-primary">
                {displayName(value)}
              </span>
              {/* Wraps rather than truncating: with the MRN last, an ellipsis would cut it off. */}
              <span className={`block ${V2_META}`} data-testid="patient-picker-identity">
                {identityFacts(value, { includeContact: false })}
              </span>
            </span>
          </span>
        ) : (
          <span className="text-text-muted">{placeholder}</span>
        )}
        <ChevronDown className="size-4 text-text-secondary shrink-0" />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }}
        className="pa-picker-pop p-0 w-anchor min-w-80 gap-3"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, MRN, phone…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isFetching && rows.length === 0 ? (
              <div className="p-2 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : rows.length === 0 ? (
              <CommandEmpty>
                {searchQuery
                  ? 'No patients match this search.'
                  : `Type at least ${MIN_SEARCH_CHARS} characters to search…`}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {rows.map((p) => {
                  const id = p.patientId ?? `${p.mrn ?? 'no-mrn'}-${p.firstName ?? ''}`;
                  const selected = value?.patientId === p.patientId;
                  return (
                    <CommandItem
                      key={id}
                      value={id}
                      onSelect={() => {
                        onChange(p);
                        setOpen(false);
                      }}
                      className="pa-picker-row flex items-center gap-3 px-2 py-2"
                      data-checked={selected}
                    >
                      <Avatar
                        scale="compact"
                        tone="patient"
                        name={displayName(p)}
                        src={p.image ?? undefined}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {displayName(p)}
                        </p>
                        {/* Wraps (no truncate): the MRN is last, so an ellipsis would hide it. */}
                        <p className={V2_META}>
                          {identityFacts(p, { includeContact: true })}
                        </p>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}
